import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createCoreTag,
  deleteCoreTag,
  fetchTagUsage,
  listCoreTags,
  TAG_COLORS,
  TAG_SCOPES,
  SUGGESTED_TAGS,
  tagColorClass,
  updateCoreTag,
  type CoreTag,
} from '@/lib/coreSocle';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function TagsTab({ org }: Props) {
  const [tags, setTags] = useState<CoreTag[]>([]);
  const [usage, setUsage] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{ id: number | null; name: string; color: string; scope: string }>({ id: null, name: '', color: 'secondary', scope: 'both' });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [scopeFilter, setScopeFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [t, u] = await Promise.all([listCoreTags(org.id), fetchTagUsage(org.id)]);
      setTags(t);
      setUsage(u);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const openCreate = (name = '') => {
    setForm({ id: null, name, color: 'secondary', scope: 'both' });
    setOpen(true);
  };

  const openEdit = (t: CoreTag) => {
    setForm({ id: t.id, name: t.name, color: t.color || 'secondary', scope: t.scope || 'both' });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (form.id) await updateCoreTag(form.id, { name: form.name.trim(), color: form.color, scope: form.scope });
      else await createCoreTag({ tenantId: org.tenant_id, organizationId: org.id, name: form.name.trim(), color: form.color, scope: form.scope });
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (t: CoreTag) => {
    setBusyId(t.id);
    try {
      await updateCoreTag(t.id, { status: t.status === 'inactive' ? 'active' : 'inactive' });
      load();
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (t: CoreTag) => {
    setBusyId(t.id);
    try {
      await deleteCoreTag(t.id);
      load();
    } finally {
      setBusyId(null);
    }
  };

  const visible = scopeFilter === 'all' ? tags : tags.filter((t) => t.scope === scopeFilter);
  const existingNames = new Set(tags.map((t) => t.name.toLowerCase()));

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          {[{ key: 'all', label: 'Tous' }, ...TAG_SCOPES.map((s) => ({ key: s.key, label: s.label }))].map((f) => (
            <button
              key={f.key}
              onClick={() => setScopeFilter(f.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${scopeFilter === f.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={() => openCreate()} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Nouveau tag
        </button>
      </div>

      <div className="mb-5">
        <p className="text-xs text-foreground-500 mb-2">Suggestions en un clic</p>
        <div className="flex flex-wrap gap-2">
          {SUGGESTED_TAGS.map((s) => (
            <button
              key={s}
              onClick={() => openCreate(s)}
              disabled={existingNames.has(s.toLowerCase())}
              className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <i className="ri-add-line mr-1"></i>{s}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-hashtag text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucun tag pour ce filtre.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {visible.map((t) => {
            const inactive = t.status === 'inactive';
            const scopeLabel = TAG_SCOPES.find((s) => s.key === t.scope)?.label || 'Produits & services';
            return (
              <div key={t.id} className={`bg-background-50 border border-background-200/70 rounded-lg p-4 ${inactive ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-semibold ${tagColorClass(t.color)}`}>
                    <i className="ri-price-tag-2-line"></i>{t.name}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">{usage[t.id] || 0} usage{usage[t.id] === 1 ? '' : 's'}</span>
                </div>
                <p className="text-xs text-foreground-500 mb-3 flex items-center gap-1.5"><i className="ri-focus-3-line"></i>{scopeLabel}{t.slug && <span className="font-mono text-foreground-400">· #{t.slug}</span>}</p>
                <div className="flex items-center gap-2 pt-3 border-t border-background-200/60">
                  <button onClick={() => openEdit(t)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"><i className="ri-edit-line"></i>Modifier</button>
                  <button onClick={() => toggleStatus(t)} disabled={busyId === t.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap">
                    <i className={inactive ? 'ri-eye-line' : 'ri-eye-off-line'}></i>{inactive ? 'Réactiver' : 'Désactiver'}
                  </button>
                  <button onClick={() => remove(t)} disabled={busyId === t.id} className="ml-auto w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier le tag' : 'Nouveau tag'}
          onClose={() => setOpen(false)}
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving || !form.name.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Nom du tag</span>
              <input className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : premium" autoFocus />
            </label>
            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Couleur</span>
              <div className="flex flex-wrap gap-2">
                {TAG_COLORS.map((c) => (
                  <button key={c.key} type="button" onClick={() => setForm({ ...form, color: c.key })} className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors cursor-pointer ${c.className} ${form.color === c.key ? 'ring-2 ring-primary-300' : ''}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">S’applique à</span>
              <select className={INPUT} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                {TAG_SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}